/**
 * Line Interpolation Utilities
 *
 * Provides interpolation functions for different line types.
 * Converts simple point arrays into smooth curves.
 *
 * Supported interpolation types:
 * - Straight: No interpolation (direct point-to-point lines)
 * - Curved: Smooth curves using Catmull-Rom spline (subtle transitions)
 *
 * @module components/map/GeometryRenderer/LineRenderer/utils/lineInterpolation
 */

import { LineType } from '../../../../../types/geometry';
import {
  MIN_POINTS_FOR_INTERPOLATION,
  CATMULL_ROM_SEGMENTS,
} from '../../../../../constants';
import type { Coordinates } from '../../../../../types';

/**
 * Interpolate line points based on line type
 *
 * Main dispatcher function that applies appropriate interpolation algorithm
 * based on the specified line type.
 *
 * @param points - Array of coordinates defining the line
 * @param lineType - Type of interpolation to apply
 * @returns Interpolated array of coordinates
 *
 * @example
 * ```typescript
 * const points = [
 *   { lat: 52.0, lng: 21.0 },
 *   { lat: 52.1, lng: 21.1 },
 *   { lat: 52.2, lng: 21.2 },
 * ];
 * const smoothed = interpolateLinePoints(points, LineType.Curved);
 * // Returns smooth Catmull-Rom curve through points
 * ```
 */
export function interpolateLinePoints(
  points: Coordinates[],
  lineType: LineType
): Coordinates[] {
  // Validation: need at least 2 points for any line
  if (points.length < MIN_POINTS_FOR_INTERPOLATION) {
    return points;
  }

  switch (lineType) {
    case LineType.Curved:
      return interpolateCurved(points);
    case LineType.Straight:
    default:
      // No interpolation for straight lines
      return points;
  }
}

/**
 * Interpolate points using Catmull-Rom spline (Curved line type)
 *
 * Creates a smooth interpolating curve that passes through all points
 * with continuous tangents (C1 continuity). Uses Catmull-Rom spline algorithm.
 *
 * Algorithm:
 * - For each segment between p1 and p2, uses 4 points: p0, p1, p2, p3
 * - p0 and p3 are control points that determine curvature
 * - Curve interpolates between p1 and p2 (passes through them)
 * - Generates CATMULL_ROM_SEGMENTS points per segment for smoothness
 *
 * Edge cases:
 * - First segment: duplicates first point [p0, p0, p1, p2]
 * - Last segment: duplicates last point [pN-2, pN-1, pN, pN]
 *
 * @param points - Array of coordinates to interpolate
 * @returns Smooth curve passing through all points
 *
 * @example
 * ```typescript
 * const points = [
 *   { lat: 52.0, lng: 21.0 },
 *   { lat: 52.1, lng: 21.1 },
 *   { lat: 52.2, lng: 21.0 },
 * ];
 * const smooth = interpolateArc(points);
 * // Returns smooth curve through all points with C1 continuity
 * ```
 */
function interpolateCurved(points: Coordinates[]): Coordinates[] {
  // Need at least 2 points for interpolation
  if (points.length < 2) {
    return points;
  }

  // Single segment - just return straight line
  if (points.length === 2) {
    return points;
  }

  const result: Coordinates[] = [];

  // Process each segment (between consecutive points)
  for (let i = 0; i < points.length - 1; i++) {
    // Get 4 points for Catmull-Rom: p0, p1, p2, p3
    // We interpolate between p1 and p2
    let p0, p1, p2, p3;

    if (i === 0) {
      // First segment: duplicate first point
      p0 = points[0];
      p1 = points[0];
      p2 = points[1];
      p3 = points[2] || points[1]; // Handle case with only 2 points
    } else if (i === points.length - 2) {
      // Last segment: duplicate last point
      p0 = points[i - 1];
      p1 = points[i];
      p2 = points[i + 1];
      p3 = points[i + 1];
    } else {
      // Middle segments: use 4 consecutive points
      p0 = points[i - 1];
      p1 = points[i];
      p2 = points[i + 1];
      p3 = points[i + 2];
    }

    if (!p0 || !p1 || !p2 || !p3) continue;

    // Generate interpolated points for this segment
    const segmentPoints = catmullRomSegment(
      p0,
      p1,
      p2,
      p3,
      CATMULL_ROM_SEGMENTS
    );

    // Add all segment points except the last (to avoid duplicates between segments)
    result.push(...segmentPoints.slice(0, -1));
  }

  // Add the final point
  const lastPoint = points[points.length - 1];
  if (lastPoint) {
    result.push(lastPoint);
  }

  return result;
}

/**
 * Calculate Catmull-Rom spline segment
 *
 * Generates a smooth curve segment between p1 and p2 using Catmull-Rom spline formula.
 * Uses p0 and p3 as control points to determine curvature and tangents.
 *
 * Catmull-Rom formula:
 * P(t) = 0.5 * [
 *   (2 * p1) +
 *   (-p0 + p2) * t +
 *   (2*p0 - 5*p1 + 4*p2 - p3) * t² +
 *   (-p0 + 3*p1 - 3*p2 + p3) * t³
 * ]
 *
 * Uses the standard 0.5-factor formula (no tension parameter).
 *
 * @param p0 - Control point before p1
 * @param p1 - Start point of interpolation
 * @param p2 - End point of interpolation
 * @param p3 - Control point after p2
 * @param segments - Number of points to generate
 * @returns Array of interpolated coordinates forming smooth curve from p1 to p2
 *
 * @internal Exported for use in segment-based rendering (branching lines)
 */
export function catmullRomSegment(
  p0: Coordinates,
  p1: Coordinates,
  p2: Coordinates,
  p3: Coordinates,
  segments: number
): Coordinates[] {
  const result: Coordinates[] = [];

  // Generate points along the curve
  for (let i = 0; i <= segments; i++) {
    const t = i / segments; // Parameter from 0 to 1
    const t2 = t * t; // t squared
    const t3 = t2 * t; // t cubed

    // Standard Catmull-Rom spline formula
    // P(t) = 0.5 * [(2*p1) + (-p0+p2)*t + (2*p0-5*p1+4*p2-p3)*t² + (-p0+3*p1-3*p2+p3)*t³]
    const CATMULL_ROM_FACTOR = 0.5;

    // Calculate lat coordinate
    const lat =
      CATMULL_ROM_FACTOR *
      (2 * p1.lat +
        (-p0.lat + p2.lat) * t +
        (2 * p0.lat - 5 * p1.lat + 4 * p2.lat - p3.lat) * t2 +
        (-p0.lat + 3 * p1.lat - 3 * p2.lat + p3.lat) * t3);

    // Calculate lng coordinate
    const lng =
      CATMULL_ROM_FACTOR *
      (2 * p1.lng +
        (-p0.lng + p2.lng) * t +
        (2 * p0.lng - 5 * p1.lng + 4 * p2.lng - p3.lng) * t2 +
        (-p0.lng + 3 * p1.lng - 3 * p2.lng + p3.lng) * t3);

    result.push({ lat, lng });
  }

  return result;
}
